import React, { Component } from 'react';
import { ColorPicker, BasePopoverPanel } from 'crazyEditor/components/EditorUI/BaseUI/index';
import _ from 'lodash';
import { connect } from 'react-redux';
import { closeLazyPanel } from 'crazyEditor/redux/modules/panel/panel';
import PropTypes from 'prop-types';
import Radium from 'radium';
import {getAvailableFonts} from 'crazyEditor/helpers/FontLoader';
import MenuItem from 'material-ui/MenuItem';
import { setElementUpdatePreview, clearElementUpdatePreview } from 'crazyEditor/redux/modules/element/actions/previewElementActions';
import { updateDesktopElement } from 'crazyEditor/redux/modules/element/actions/desktopElementActions';
import { getElementMethods } from 'crazyEditor/core/elements/utils/elementDefinitionUtils';
import * as Colors from 'material-ui/styles/colors';
import shallowEqual from 'fbjs/lib/shallowEqual';
import KeyboardSequenceRecorder from 'shared/helpers/KeyboardSequenceRecorder';
import {TweenMax} from 'gsap';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
/* global Strong Linear SlowMo Ease TimelineMax */
import { getSelectedElements } from 'crazyEditor/redux/modules/element/elementSelectors';
import NavigationCheck from 'material-ui/svg-icons/navigation/check';
import { keymaster, pushScope, popScope } from 'shared/helpers/keymasterHelper';

const FONTS_IN_VIEW = 8;
const MENU_ITEM_HEIGHT = 48;
const SCROLL_CONTAINER_ELEMENT_ID = 'fontFamilyScrollContainer';
const SCOPE_NAME = 'keySequenceRecorder';

@connect(
  state => ({
    selectedElements: getSelectedElements(state),
  }),
  {closeLazyPanel, setElementUpdatePreview, clearElementUpdatePreview, updateDesktopElement}
)
@Radium
export default class FontFamilyPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      renderAll: false,
      hoveringSelectedFontFamily: false,
      initialSelectedFontFamily: this.getSelectedFontFamily(props),
    };
  }

  componentWillMount() {
    this.availableFonts = getAvailableFonts();
    this.scrollContainerId = _.uniqueId(SCROLL_CONTAINER_ELEMENT_ID);
  }

  componentDidMount() {
    keymaster('up, down, pageup, pagedown', SCOPE_NAME, this.handleUpDown);
    this.scrollToSelectedFont(null, false);
    if (this.props.open) {
      this.enableKeypressListener();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.open && nextProps.open) {
      this.setState({initialSelectedFontFamily: this.getSelectedFontFamily(this.props), renderAll: false, hoveringSelectedFontFamily: false});
      this.enableKeypressListener();
    }
    if (!nextProps.open) {
      this.disableKeypressListener();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !shallowEqual(nextProps, this.props) || !shallowEqual(nextState, this.state);
  }

  componentDidUpdate(prevProps) {
    if (this.scrollAfterNextUpdate) {
      this.scrollToSelectedFont();
      this.scrollAfterNextUpdate = false;
    }
    const renderAll = this.state.renderAll;
    if (this.props.open && !prevProps.open) {
      this.availableFonts = getAvailableFonts();
      this.scrollToSelectedFont(null, false);
      if (!renderAll) {
        this.deferredSetState({renderAll: true});
      }
    }
  }

  componentWillUnmount() {
    this.disableKeypressListener();
    keymaster.deleteScope(SCOPE_NAME);
  }

  onChange = (fontFamily, hoverChange = true) => {
    const { setElementUpdatePreview, open, selectedElements } = this.props;
    const element = selectedElements[0];
    const elementMethods = getElementMethods(element.type);

    if (!open) {
      return;
    }
    if (hoverChange) {
      if (element.data.compProps.fontFamily === fontFamily) {
        this.setState({hoveringSelectedFontFamily: true});
      } else {
        this.setState({hoveringSelectedFontFamily: false});
      }
    }
    setElementUpdatePreview(element.id, elementMethods.getFontFamilySetter(fontFamily));
  };

  onCancel = () => {
    const { closeLazyPanel, panelName, clearElementUpdatePreview} = this.props;

    clearElementUpdatePreview();
    closeLazyPanel(panelName);
  };

  onSelect = (fontFamily) => {
    const { closeLazyPanel, updateDesktopElement, selectedElements} = this.props;
    const element = selectedElements[0];
    const elementMethods = getElementMethods(element.type);
    updateDesktopElement(element.id, elementMethods.getFontFamilySetter(fontFamily || this.getSelectedFontFamily(this.props)));
    closeLazyPanel(this.props.panelName);
  };

  getSelectedFontFamily(props) {
    const element = props.selectedElements[0];
    return element.data.compProps.fontFamily;
  }

  handleUpDown = (event, keyMasterEvent) => {
    event.preventDefault();
    let index = this.indexOfSelectedFontFamily();
    const availableFonts = this.availableFonts;
    switch (keyMasterEvent.key) {
      case 'up':
        index--;
        break;
      case 'down':
        index++;
        break;
      case 'pageup':
        index -= FONTS_IN_VIEW;
        break;
      case 'pagedown':
        index += FONTS_IN_VIEW;
        break;
      default:
        break;
    }
    this.scrollAfterNextUpdate = true;
    index = Math.min(Math.max(index, 0), availableFonts.length - 1);
    this.onChange(availableFonts[index], false);
  };

  deferredSetState = (state) => {_.defer(() => {this.setState(state);});};

  enableKeypressListener = () => {
    if (!this.keyboardSequenceRecorder) {
      pushScope(SCOPE_NAME);
      this.keyboardSequenceRecorder = new KeyboardSequenceRecorder((value, repeat) => {
        const availableFontsCount = this.availableFonts.length;
        for (let i = 0; i < availableFontsCount; ++i) {
          const font = this.availableFonts[i];
          if (font.toLowerCase().startsWith(value.toLowerCase())) {
            if (repeat) {
              repeat--;
              continue;
            }
            this.scrollAfterNextUpdate = true;
            this.onChange(font, false);
            return;
          }
        }
        // no match, clear value
        this.keyboardSequenceRecorder.clearValue();
      }, () => _.defer(() => {
        this.onSelect();
      }));
    }
  };

  disableKeypressListener() {
    if (this.keyboardSequenceRecorder) {
      popScope();
      this.keyboardSequenceRecorder.destroy();
      this.keyboardSequenceRecorder = null;
    }
  }

  indexOfSelectedFontFamily(fontFamily) {
    return this.availableFonts.indexOf(fontFamily || this.getSelectedFontFamily(this.props));
  }


  scrollToSelectedFont(fontFamily, animate = true) {
    const offset = (this.indexOfSelectedFontFamily(fontFamily) - 3) * MENU_ITEM_HEIGHT;
    TweenMax.to(`#${this.scrollContainerId}`, animate ? 0.350 : 0, {scrollTo: {y: offset}, ease: Ease.easeInOut});
  }

  render() {
    const { panelName, anchorEl, open, selectedElements} = this.props;
    const element = selectedElements[0];
    const selectedFontFamily = _.get(element, 'data.compProps.fontFamily');
    const {renderAll, hoveringSelectedFontFamily, initialSelectedFontFamily} = this.state;
    const numberOfFontsToRender = renderAll ? this.availableFonts.length : Math.max(this.indexOfSelectedFontFamily() + (FONTS_IN_VIEW / 2) + 1, FONTS_IN_VIEW + 1);
    const numberOFFontsToSkip = renderAll ? 0 : Math.max(this.indexOfSelectedFontFamily() - FONTS_IN_VIEW / 2, 0);
    return (
      <BasePopoverPanel
        panelName={panelName}
        anchorEl={anchorEl}
        open={open}
        onRequestClose={this.onCancel}
      >
        <div
          style={{maxHeight: FONTS_IN_VIEW * MENU_ITEM_HEIGHT, position: 'relative', overflowY: 'scroll'}}
          id = {this.scrollContainerId}
        >
          <div
            style={{height: this.availableFonts.length * MENU_ITEM_HEIGHT}}
          >
            <div
              style={{height: numberOFFontsToSkip * MENU_ITEM_HEIGHT}}
            />
            {
              _.range(numberOFFontsToSkip, numberOfFontsToRender).map((index) => {
                const fontFamily = this.availableFonts[index];
                let backgroundColor;
                if (fontFamily === selectedFontFamily && !hoveringSelectedFontFamily) {
                  backgroundColor = Colors.grey100;
                }
                let leftIcon;
                let color;
                if (fontFamily === initialSelectedFontFamily) {
                  leftIcon = <NavigationCheck color={Colors.lightGreen800}/>;
                  color = Colors.lightGreen800;
                }
                return <MenuItemWrapper leftIcon = {leftIcon} onMouseEnter={this.onChange.bind(this, fontFamily)} onClick={this.onSelect.bind(this, fontFamily)} primaryText={fontFamily} key={fontFamily} innerDivStyle={{backgroundColor}} style={{height: MENU_ITEM_HEIGHT, fontFamily, width: 250, color}}/>;
              })
            }
          </div>
        </div>
      </BasePopoverPanel>
    );
  }
}

FontFamilyPanel.propTypes = {
  panelName: PropTypes.string.isRequired,
  anchorEl: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  panelProps: PropTypes.object
};

class MenuItemWrapper extends Component {
  shouldComponentUpdate(nextProps) {
    return this.props.innerDivStyle.backgroundColor !== nextProps.innerDivStyle.backgroundColor;
  }

  render() {
    return <MenuItem {...this.props}/>;
  }
}